import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowRightIcon } from 'lucide-react'

import { Button } from '@/components/ui'
import { openChatPanelOnNextVisit, requestChatPanelOpen } from '@/modules/chat'
import type { Project } from '@/modules/projects'
import { useOpenCreateProject } from '../hooks'
import { projectScreenPath, type StepAction as StepActionData } from '../steps'

interface StepActionProps {
  action: StepActionData
  project: Project | null
  theme?: 'default' | 'outline'
}

export function StepAction({
  action,
  project,
  theme = 'outline',
}: StepActionProps) {
  const openCreateProject = useOpenCreateProject()
  const navigate = useNavigate()

  if (action.kind === 'create-project') {
    return (
      <Button theme={theme} onClick={openCreateProject}>
        {action.label}
        <ArrowRightIcon />
      </Button>
    )
  }

  if (action.kind === 'add-statements') {
    return (
      <Button theme={theme} asChild>
        {project ? (
          <Link
            to="/projects/$slug/files"
            params={{ slug: project.slug }}
            preload="intent"
          >
            {action.label}
            <ArrowRightIcon />
          </Link>
        ) : (
          <Link to="/projects" preload="intent">
            Open your project
            <ArrowRightIcon />
          </Link>
        )}
      </Button>
    )
  }

  if (project === null) return null

  const to = projectScreenPath(action.screen, project.slug)

  if (action.screen === 'chat') {
    const openChat = () => {
      openChatPanelOnNextVisit(project.slug)
      navigate({ to }).then(requestChatPanelOpen)
    }
    return (
      <Button theme={theme} onClick={openChat}>
        {action.label}
        <ArrowRightIcon />
      </Button>
    )
  }

  return (
    <Button theme={theme} asChild>
      <Link to={to} preload="intent">
        {action.label}
        <ArrowRightIcon />
      </Link>
    </Button>
  )
}
